import { BaseHttpClient } from "../../http/base-http-client";
import type {
  TemplateScope,
  PowerPointTemplateResponse,
  TemplateListFilters,
  PowerPointTemplateUpdate,
  AvailableTemplatesResponse,
  TemplateUploadParams,
  PowerPointTemplateDeleteResponse,
  TemplateLoadResponse,
} from "../../types/api/powerpoint-template-types";

/**
 * PowerPoint Template API Client
 *
 * Handles all PowerPoint template management endpoints:
 * - Template CRUD operations (create, read, update, delete)
 * - Template analysis and placeholder detection
 * - Template listing with filtering
 * - Usage analytics and statistics
 * - Template loading for processing
 *
 * Follows SRP: Single responsibility for template operations
 * Follows SOC: Separated from business logic and UI concerns
 */

/**
 * PowerPoint Template API Client
 *
 * Handles all PowerPoint template management endpoints:
 * - Template CRUD operations (create, read, update, delete)
 * - Template analysis and placeholder detection
 * - Template listing with filtering
 * - Usage analytics and statistics
 * - Template loading for processing
 *
 * Follows SRP: Single responsibility for template operations
 * Follows SOC: Separated from business logic and UI concerns
 */
export class PowerPointTemplateApiClient extends BaseHttpClient {
  // ==================== TEMPLATE CRUD ====================

  /**
   * Upload a new PowerPoint template
   * @param params - Template upload parameters including file and metadata
   * @returns Promise<PowerPointTemplateResponse> - Created template information
   */
  async uploadTemplate(
    params: TemplateUploadParams
  ): Promise<PowerPointTemplateResponse> {
    const formData = new FormData();

    // Add file
    formData.append("file", params.file);

    // Add metadata
    formData.append("display_name", params.display_name);
    if (params.description) {
      formData.append("description", params.description);
    }
    if (params.category) {
      formData.append("category", params.category);
    }
    formData.append("scope", params.scope);
    if (params.agent_id) {
      formData.append("agent_id", params.agent_id);
    }
    if (params.shared_scope_id) {
      formData.append("shared_scope_id", params.shared_scope_id);
    }
    formData.append("is_default", String(params.is_default));
    formData.append("is_public", String(params.is_public));

    return this.post<PowerPointTemplateResponse>(
      "/powerpoint-templates/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  }

  /**
   * Request a presigned upload URL for PowerPoint template upload
   * @param params - Template upload parameters including file metadata
   * @returns Promise containing upload URL and metadata
   */
  async requestUploadUrl(params: {
    display_name: string;
    file_size: number;
    content_type: string;
    template_name: string;
    description?: string;
    category?: string;
    scope: TemplateScope;
    agent_id?: string;
    shared_scope_id?: string;
    is_default?: boolean;
    is_public?: boolean;
  }): Promise<{
    upload_url: string;
    s3_path: string;
    template_metadata: Record<string, any>;
  }> {
    return this.post("powerpoint-templates/request-upload-url", undefined, {
      params,
    });
  }

  /**
   * Complete template upload after file has been uploaded to S3
   * @param s3_path - The S3 path where the file was uploaded
   * @param template_metadata - The metadata returned from requestUploadUrl
   * @returns Promise<PowerPointTemplateResponse> - Created template information
   */
  async completeUpload(
    s3_path: string,
    template_metadata: Record<string, any>
  ): Promise<PowerPointTemplateResponse> {
    return this.post<PowerPointTemplateResponse>(
      "/powerpoint-templates/complete-upload",
      undefined,
      {
        params: {
          s3_path,
          template_metadata: JSON.stringify(template_metadata),
        },
      }
    );
  }

  /**
   * Get list of templates with optional filtering
   * @param filters - Optional filtering parameters
   * @returns Promise<PowerPointTemplateResponse[]> - List of templates
   */
  async getTemplates(
    filters?: TemplateListFilters
  ): Promise<PowerPointTemplateResponse[]> {
    const queryParams: Record<string, any> = {};

    if (filters?.scope) queryParams.scope = filters.scope;
    if (filters?.agent_id) queryParams.agent_id = filters.agent_id;
    if (filters?.shared_scope_id)
      queryParams.shared_scope_id = filters.shared_scope_id;
    if (filters?.category) queryParams.category = filters.category;
    if (filters?.include_inactive)
      queryParams.include_inactive = filters.include_inactive;

    return this.get<PowerPointTemplateResponse[]>(
      "/powerpoint-templates",
      queryParams
    );
  }

  /**
   * Get all templates available to current agent with priority information
   * @param agentId - Agent ID for agent-specific templates
   * @param sharedScopeId - Shared scope ID for organization templates
   * @returns Promise<AvailableTemplatesResponse> - Templates organized by scope with priority
   */
  async getAvailableTemplates(
    agentId?: string,
    sharedScopeId?: string
  ): Promise<AvailableTemplatesResponse> {
    const params: Record<string, string> = {};
    if (agentId) params.agent_id = agentId;
    if (sharedScopeId) params.shared_scope_id = sharedScopeId;

    return this.get<AvailableTemplatesResponse>(
      "/powerpoint-templates/available",
      params // Pass params directly, not wrapped in { params }
    );
  }

  /**
   * Get the default template for a specific scope
   * @param scope - Template scope to get default from
   * @param agentId - Agent ID for agent scope
   * @param sharedScopeId - Shared scope ID for shared scope
   * @returns Promise<PowerPointTemplateResponse> - Default template
   */
  async getDefaultTemplate(
    scope: TemplateScope,
    agentId?: string,
    sharedScopeId?: string
  ): Promise<PowerPointTemplateResponse> {
    const queryParams: Record<string, any> = { scope };
    if (agentId) queryParams.agent_id = agentId;
    if (sharedScopeId) queryParams.shared_scope_id = sharedScopeId;

    return this.get<PowerPointTemplateResponse>(
      "/powerpoint-templates/default",
      queryParams
    );
  }

  /**
   * Get a specific template by ID
   * @param templateId - Template ID to retrieve
   * @returns Promise<PowerPointTemplateResponse> - Template information
   */
  async getTemplate(templateId: string): Promise<PowerPointTemplateResponse> {
    return this.get<PowerPointTemplateResponse>(
      `/powerpoint-templates/${templateId}`
    );
  }

  /**
   * Update template metadata
   * @param templateId - Template ID to update
   * @param updates - Partial template updates
   * @returns Promise<PowerPointTemplateResponse> - Updated template
   */
  async updateTemplate(
    templateId: string,
    updates: PowerPointTemplateUpdate
  ): Promise<PowerPointTemplateResponse> {
    return this.patch<PowerPointTemplateResponse>(
      `/powerpoint-templates/${templateId}`,
      updates
    );
  }

  /**
   * Delete a template
   * @param templateId - Template ID to delete
   * @param hardDelete - Whether to permanently delete (vs soft delete). Defaults to true since there's no restore functionality.
   * @returns Promise<PowerPointTemplateDeleteResponse> - Deletion confirmation
   */
  async deleteTemplate(
    templateId: string,
    hardDelete: boolean = true
  ): Promise<PowerPointTemplateDeleteResponse> {
    const queryParams: Record<string, any> = {};
    if (hardDelete !== undefined) queryParams.hard_delete = hardDelete;

    return this.delete<PowerPointTemplateDeleteResponse>(
      `/powerpoint-templates/${templateId}`,
      queryParams
    );
  }

  // ==================== TEMPLATE LOADING ====================

  /**
   * Load template for PowerPoint processing (internal endpoint)
   * @param templateName - Template filename to load
   * @param agentId - Optional agent ID for agent-specific templates
   * @param sharedScopeId - Optional shared scope ID for organization templates
   * @returns Promise<TemplateLoadResponse> - Loading result
   */
  async loadTemplateForProcessing(
    templateName: string,
    agentId?: string,
    sharedScopeId?: string
  ): Promise<TemplateLoadResponse> {
    const queryParams: Record<string, any> = {};
    if (agentId) queryParams.agent_id = agentId;
    if (sharedScopeId) queryParams.shared_scope_id = sharedScopeId;

    return this.post<TemplateLoadResponse>(
      `/powerpoint-templates/load/${encodeURIComponent(templateName)}`,
      {},
      { params: queryParams }
    );
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Format file size for display
   * @param bytes - File size in bytes
   * @returns string - Formatted file size (e.g., "2.5 MB")
   */
  static formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Get scope display name
   * @param scope - Template scope
   * @returns string - Human-readable scope name
   */
  static getScopeDisplayName(scope: TemplateScope): string {
    switch (scope) {
      case "global":
        return "System Template";
      case "shared":
        return "Organization Template";
      case "agent":
        return "Agent Template";
      default:
        return "Unknown Scope";
    }
  }

  /**
   * Get scope description
   * @param scope - Template scope
   * @returns string - Scope description
   */
  static getScopeDescription(scope: TemplateScope): string {
    switch (scope) {
      case "global":
        return "Available to all agents system-wide";
      case "shared":
        return "Available to agents in your organization";
      case "agent":
        return "Only available to this specific agent";
      default:
        return "";
    }
  }

  /**
   * Validate template file
   * @param file - File to validate
   * @returns boolean - Whether file is valid PowerPoint file
   */
  static validateTemplateFile(file: File): { valid: boolean; error?: string } {
    // Check file type - accept both presentations and templates
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
      "application/vnd.openxmlformats-officedocument.presentationml.template", // .potx
      "application/vnd.ms-powerpoint", // .ppt (legacy)
    ];

    // Check file extension (browsers may not send correct MIME types)
    const validExtensions = [".pptx", ".potx", ".ppt"];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext =>
      fileName.endsWith(ext)
    );

    if (!validTypes.includes(file.type) && !hasValidExtension) {
      return {
        valid: false,
        error: "Only PowerPoint files (.pptx, .potx, .ppt) are allowed",
      };
    }

    // Check file size (50MB limit)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error: "File size must be less than 50MB",
      };
    }

    return { valid: true };
  }

  /**
   * Get placeholder summary for display
   * @param placeholders - Template placeholder data
   * @returns string - Human-readable placeholder summary
   */
  static getPlaceholderSummary(placeholders: Record<string, any>): string {
    const summary = placeholders?.summary || {};
    const parts = Object.entries(summary)
      .map(([key, count]) => `${key}: ${count}`)
      .slice(0, 3); // Show first 3 types

    if (parts.length === 0) return "No placeholders detected";

    const summaryStr = parts.join(", ");
    const remaining = Object.keys(summary).length - parts.length;

    return remaining > 0 ? `${summaryStr} (+${remaining} more)` : summaryStr;
  }
}

/**
 * Singleton instance and factory function for PowerPoint template operations
 */
export const powerPointTemplateApiClient = new PowerPointTemplateApiClient();
export const createPowerPointTemplateApiClient = () =>
  new PowerPointTemplateApiClient();
